<template>
    <div
        v-if="showDropdown && props.editor"
        class="bubble-dropdown"
        role="menu"
        aria-label="Study Assistant Dropdown"
        :style="dropdownStyle"
        @mousedown.prevent
        @click.stop
    >
        <div
            v-for="(action, index) in filteredActions"
            :key="index"
            :class="{
                'bubble-dropdown__option': true,
                'bubble-dropdown__action': true,
                'bubble-dropdown__disabled': action.isDisabled
            }"
            role="menuitem"
            :aria-label="action.name"
            :tabindex="0"
            @click="() => {
                if (action.isDisabled) return;
                action.type === 'action' && action.onclick(props.editor)
            }"
            @mouseenter="mouseEnterEvent(action, index)"
            @mouseleave="mouseLeaveEvent()"
            :data-tooltip="action.isDisabled ? action.disabledReason : ''"
        >
            <template v-if="action.type === 'group'">
                {{ action.name }}
                <i class="pi pi-chevron-right" aria-hidden="true"/>
                <div
                    v-if="showChildMenu === index"
                    class="bubble-dropdown bubble-dropdown--child"
                    role="menu"
                    :aria-label="`${action.name} submenu`"
                >
                    <div
                        v-for="(action, index) in childMenuActions"
                        :key="index"
                        :class="{
                            'bubble-dropdown__option': true,
                            'bubble-dropdown__action': action.type === 'action',
                            'bubble-dropdown__heading': action.type === 'heading'
                        }"
                        @click="() => {
                            action.type === 'action' && action.onclick(props.editor)
                        }"
                    >
                        <template v-if="action.type === 'action'">
                            {{ action.name }}
                            <img :src="action.image" alt="icon" />
                        </template>
                        <template v-else-if="action.type === 'heading'">
                            <img :src="action.image" alt="icon" />
                            {{ action.name }}
                        </template>
                        <template v-else-if="action.type === 'colors'">
                            <div
                                v-for="(color, index) in action.children"
                                :key="index"
                                class="bubble-dropdown__color"
                                role="button"
                                :aria-label="`Apply ${color.name} color`"
                                :tabindex="0"
                                :style="{ backgroundColor: color.name }"
                                @click="() => {color.type === 'action' && color.onclick()}"
                            />
                        </template>
                    </div>
                </div>
            </template>
            <template v-else-if="action.type === 'action'">
                {{ action.name }}
                <img :src="action.image" alt="icon" />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Editor } from '@tiptap/vue-3';
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { Action } from '@/types/study-assistant-interfaces';


const props = defineProps<{
    editor: Editor,
    actions: Action[],
    hideDropdown: boolean,
    tiptapToken: string,
    editorName: string,
    proUser: boolean,
}>();

const emit = defineEmits(['updateAudioVideoTime']);

let selectionUpdateHandler: (() => void) | null = null;
const showDropdown = ref(false);

onMounted(() => {
    if (props.editor) {
        selectionUpdateHandler = () => shouldShow();
        props.editor.on('selectionUpdate', selectionUpdateHandler);
        props.editor.on('focus', selectionUpdateHandler);
        props.editor.on('blur', () => {
            // Add delay to prevent dropdown from hiding during clicks
            setTimeout(() => {
                showDropdown.value = false;
            }, 200);
        });
    }
});

const isHidden = ref<number[]>([]);

const filteredActions = computed(() => {
    let filteredByEditor = props.actions;
    
    // Filter actions based on editor type
    if (props.editorName === 'transcript') {
        // Remove "Rewrite" action from transcript editor
        filteredByEditor = props.actions.map(action => {
            if (action.type === 'group' && action.children) {
                return {
                    ...action,
                    children: action.children.filter(child => 
                        !(child.type === 'action' && ['Rewrite', 'Proofread'].includes(child.name))
                    )
                };
            }
            return action;
        });
    } else if (props.editorName === 'note') {
        // Remove "Simplify" action from note editor
        filteredByEditor = props.actions.map(action => {
            if (action.type === 'group' && action.children) {
                return {
                    ...action,
                    children: action.children.filter(child => 
                        !(child.type === 'action' && ['Simplify', 'Define'].includes(child.name))
                    )
                };
            }
            return action;
        });
    }
    
    if (isHidden.value.length > 0) {
        // If isHidden is not empty, filter out items at that index.
        return filteredByEditor.filter(
            (_, index) => {
                return !isHidden.value.includes(index);
            }
        );
    }
    return filteredByEditor;
});

const jumpToBookmark = ref(-1);
let showTimeout: ReturnType<typeof setTimeout> | null = null;

watch(jumpToBookmark, (newValue) => {
    emit('updateAudioVideoTime', newValue);
    jumpToBookmark.value = -1;
});

function shouldShow() {
    // Clear any existing timeout
    if (showTimeout) {
        clearTimeout(showTimeout);
        showTimeout = null;
    }

    if (props.hideDropdown || !props.editor) {
        showDropdown.value = false;
        return;
    }

    const { state } = props.editor;
    const parentNode = state.selection.$anchor.parent;
    const selectedText = state.doc.textBetween(
        state.selection.from,
        state.selection.to,
        '\n'
    );
    
    // Analyze selection to determine bookmark status
    const selectionAnalysis = analyzeSelection(state);
    const { isMixedSelection, isOnlyBookmark, isOnlyNonBookmark } = selectionAnalysis;

    // Define visibility rules for menu items
    const visibilityRules = [
        // AI tools (index 0)
        { 
            index: 0, 
            show: props.tiptapToken && props.proUser 
        },
        // Bookmark tools (index 1)
        { 
            index: 1, 
            show: isOnlyBookmark && !isMixedSelection 
        },
        // Add bookmark (index 3)
        { 
            index: 3, 
            show: props.editorName === 'transcript' ? isOnlyNonBookmark : false 
        },
        // Read aloud (index 5)
        { 
            index: 5, 
            show: props.editorName === 'transcript' && props.proUser 
        }
    ];

    // Apply visibility rules
    visibilityRules.forEach(rule => {
        updateIsHidden(rule.index, rule.show ? 'remove' : 'add');
    });

    // Handle bookmark jump
    if (isOnlyBookmark) {
        jumpToBookmark.value = parseInt(parentNode.attrs.id);
    }

    // Show dropdown if conditions are met
    const shouldShowDropdown = props.editor.isFocused && (isOnlyBookmark || selectedText.trim().length > 0);
    
    if (shouldShowDropdown) {
        clearDropdown();
        showTimeout = setTimeout(() => {
            updateDropdownPosition();
            showDropdown.value = true;
        }, 200);
    } else {
        showDropdown.value = false;
    }
}

function analyzeSelection(state: any) {
    let hasBookmark = false;
    let hasNonBookmark = false;

    state.doc.nodesBetween(state.selection.from, state.selection.to, (node: any) => {
        if (node.type.name === 'paragraph') {
            const childNode = node.firstChild;
            const isBookmark = props.editorName === 'transcript' && 
                node.attrs.id !== null && 
                childNode && 
                childNode.marks.length > 0;

            if (isBookmark) {
                hasBookmark = true;
            } else {
                hasNonBookmark = true;
            }
        }
    });

    return {
        isMixedSelection: hasBookmark && hasNonBookmark,
        isOnlyBookmark: hasBookmark && !hasNonBookmark,
        isOnlyNonBookmark: !hasBookmark && hasNonBookmark
    };
}

function updateIsHidden(index: number, action: string) {
    if (action === 'add') {
        !isHidden.value.includes(index) && isHidden.value.push(index);
    } else if (action === 'remove') {
        const idx = isHidden.value.indexOf(index);
        if (idx > -1) {
            isHidden.value.splice(idx, 1);
        }
    }   
}

const dropdownStyle = ref<{
    position: 'absolute' | 'fixed';
    top: string;
    left: string;
    zIndex: number;
    visibility: 'visible' | 'hidden';
}>({
    position: 'absolute',
    top: '0px',
    left: '0px',
    zIndex: 9999,
    visibility: 'hidden'
});

let lastKnownPosition = {
    top: 0,
    left: 0
};

function updateDropdownPosition() {
    if (!props.editor) return;
    
    const { state, view } = props.editor;
    const { from, to } = state.selection;

    const length = filteredActions.value.length;
    
    try {
        // Use DOM selection for more reliable positioning with collaboration
        const selection = window.getSelection();
        let top = 0;
        let left = 0;

        if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            
            // Check if we have a valid selection rectangle
            if (rect && (rect.width > 0 || rect.height > 0)) {
                // For long selections, use the start of the selection
                left = Math.max(
                    Math.min(
                        rect.left + rect.width / 2 - 100,
                        window.innerWidth - 220
                    ),
                    10
                );
                
                top = rect.top - (length * 50);
                if (top < 50) {
                    top = rect.bottom;
                }
            }
        } else {
            // Fallback to TipTap coordinates if DOM selection fails
            const start = view.coordsAtPos(from);
            const end = view.coordsAtPos(to);
            
            left = Math.max((start.left + end.left) / 2 - 100, 10);
            top = start.top - (length * 50);
            if (top < 50) {
                top = end.top;
            }
        }

        const positionChanged = Math.abs(top - lastKnownPosition.top) > 25 || Math.abs(left - lastKnownPosition.left) > 25;
        
        if (positionChanged) {
            lastKnownPosition = { top, left };
            dropdownStyle.value = {
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                zIndex: 9999,
                visibility: 'visible'
            };
        }
    } catch (error) {
        console.warn('Error positioning dropdown:', error);
    }
}

const showChildMenu = ref(-1);
const childMenuActions = ref<Action[]>([]);

function mouseEnterEvent(action: Action, index: number) {
    if (action.type === 'group') {
        showChildMenu.value = index;
        childMenuActions.value = action.children;
    }
}

function mouseLeaveEvent() {
    clearDropdown();
}

function clearDropdown() {
    showChildMenu.value = -1;
    childMenuActions.value = [];
}

onUnmounted(() => {
    if (props.editor && selectionUpdateHandler) {
        props.editor.off('selectionUpdate', selectionUpdateHandler);
        props.editor.off('focus', selectionUpdateHandler);
        props.editor.off('blur', selectionUpdateHandler);
    }
});
</script>

<style scoped lang="scss">
.bubble-dropdown {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 22.4rem;
    max-height: 26rem;
    border: 0.5px solid map-get($colours, accent-purple-2);
    border-radius: 0.6rem;
    padding: 0.8rem 0;
    font-family: $font-secondary;
    font-size: 1.6rem;
    background-color: map-get($colours, accent-purple-3);

    &__option {
        display: flex;
        align-items: center;
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
        padding: 0.8rem 1.6rem;
        font-size: 1.6rem;
        color: map-get($colours, white);
        background-color: transparent;

        &:focus {
            outline: 1px solid map-get($colours, accent-purple-2);
            outline-offset: 1px;
        }
    }

    &__action {

        &:hover {
            cursor: pointer;
            background-color: map-get($colours, accent-purple-2);
        }

        &:focus {
            outline: 1px solid map-get($colours, accent-purple-2);
        }
    }

    &__disabled {
        position: relative;
        cursor: not-allowed;
        color: rgba(255, 255, 255, 0.5);

        img {
            opacity: 0.5;
        }

        &:hover {
            background-color: transparent;
        }

        &:focus {
            outline: none;
        }

        &[data-tooltip]:hover::after {
            content: attr(data-tooltip);
            position: absolute;
            top: 50%;
            left: 100%;
            transform: translateY(-50%);
            margin-left: 10px;
            padding: 5px 10px;
            border-radius: 8px;
            background-color: map-get($colours, white);
            color: map-get($colours, black);
            font-size: 1.2rem;
            z-index: 10;
            width: 20rem;
        }
    }

    &__heading {
        display: flex;
        justify-content: flex-start;
        gap: 0.8rem;
        color: #e993ff;
    }

    &__color {
        border-radius: 50%;
        width: 2.4rem;
        aspect-ratio: 1;

        &:hover {
            cursor: pointer;
            opacity: 0.8;
        }

        &:focus {
            outline: 1px solid map-get($colours, accent-purple-2);
            outline-offset: 1px;
        }
    }

    &--child {
        position: absolute;
        top: 0;
        left: 22rem;
    }
}
</style>
